import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Controls, Panel, ReactFlowInstance, Node } from 'reactflow';
import 'reactflow/dist/style.css';
import VulnerabilityNode from './VulnerabilityNode';
import { DependencyEdge, DependencyNode } from '../../../types/issues-types';
import { Theme, useTheme } from '@mui/material';
//import VulnerabilityDrawer from './VulnerabilityDrawer';
import VulnerabilitySwitcher from './VulnerabilitySwitcher';
import { getFormattedData, getGraphByCondition } from './utils';
import NodeFilter from './NodeFilter';
import { makeStyles } from 'tss-react/mui';

const VulnerabilityGraph: FC<
  React.PropsWithChildren<VulnerabilityGraphProps>
> = ({ nodes, edges }) => {
  const theme = useTheme();
  const nodeTypes = useMemo(() => ({ special: VulnerabilityNode }), []);
  const [showAll, setShowAll] = useState(true);
  const { classes } = useStyles();
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);

  const setInstance = (instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance;
  };

  const toggleAll = useCallback(() => {
    setShowAll(prev => !prev);
    setFilterValue('');
  }, []);

  const [filterValue, setFilterValue] = useState('');
  const onFilterChange = (value: string) => {
    setFilterValue(value);
    const { edges, nodes } = getGraphByCondition(
      showAll ? allNodes : vulnerableNodes,
      showAll ? allEdges : vulnerableEdges,
      (node?: Node) =>
        node?.id?.toLowerCase()?.indexOf(value.toLowerCase()) !== -1,
    );
    setNodes(nodes);
    setEdges(edges);
    setTimeout(() => reactFlowInstance?.current?.fitView(), 100);
  };
  const { allNodes, allEdges, vulnerableNodes, vulnerableEdges } = useMemo(
    () => getFormattedData(nodes, edges),
    [nodes, edges],
  );
  const [edgesToShow, setEdges] = useState(allEdges);
  const [nodesToShow, setNodes] = useState(allNodes);

  useEffect(() => {
    if (showAll === true) {
      setNodes(allNodes);
      setEdges(allEdges);
    }
    if (showAll === false) {
      setNodes(vulnerableNodes);
      setEdges(vulnerableEdges);
    }
    setTimeout(() => reactFlowInstance?.current?.fitView(), 100);
  }, [allEdges, allNodes, showAll, vulnerableEdges, vulnerableNodes]);

  // const [drawerOpen, setDrawerOpen] = useState(false);

  // const onNodeClick = useCallback((_, node: Node) => {
  //   if (node.data.vulnerable) {
  //     setDrawerOpen(prev => !prev);
  //   }
  // }, []);

  // const closeDrawer = useCallback(() => {
  //   setDrawerOpen(false);
  // }, []);

  return (
    <ReactFlow
      fitView
      className={classes.content}
      onInit={setInstance}
      // onNodeClick={onNodeClick}
      style={{
        backgroundColor:
          theme.palette.mode === 'light'
            ? '#e5e5e5'
            : theme.palette.background.default,
      }}
      maxZoom={2}
      minZoom={0.1}
      nodeTypes={nodeTypes}
      proOptions={{ hideAttribution: true }}
      nodes={nodesToShow}
      edges={edgesToShow}>
      <Controls />
      <Panel position='top-left'>
        <VulnerabilitySwitcher checked={showAll} handleChange={toggleAll} />
      </Panel>
      <Panel position='top-right'>
        <NodeFilter value={filterValue} onFilterChange={onFilterChange} />
      </Panel>
      {/* <Panel position='top-right' style={{ margin: 0, height: '100%' }}>
        {drawerOpen && (
          <VulnerabilityDrawer
            handleClose={closeDrawer}
            properties={[
              'Publically Available Exploit',
              'Library Has Vulnerable Functions',
            ]}
            direct={0}
            indirect={0}
            packageName={'Test'}
            vulnerabilityType={'XSS'}
          />
        )}
      </Panel> */}
    </ReactFlow>
  );
};

export interface VulnerabilityGraphProps {
  nodes: DependencyNode[];
  edges: DependencyEdge[];
}

const useStyles = makeStyles()((theme: Theme) => ({
  content: {
    height: '100%',
  },
}));

export default VulnerabilityGraph;
